/*
  RBT Visuals – scroll reveal for the RBT sections (cards, steps, reviews, headings).
  Only elements that start below the screen are animated, so nothing visible on load ever
  flashes or shifts. Skipped entirely when the visitor asks for reduced motion, in the
  theme editor, or without IntersectionObserver. Styles live in rbt-overrides.css.
  The delays timeline (rbt-delais) runs its own step-by-step reveal and is not listed here.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Cart icon bounces when its item count changes. Watching the icon itself covers every way
  // of adding (Dwell's form, the RBT options form, quick add), which do not all fire an event.
  const watchCartIcons = () => {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      let last = icon.textContent.trim();
      new MutationObserver(() => {
        const now = icon.textContent.trim();
        if (now === last) return;
        last = now;
        icon.classList.remove('rbt-bump');
        void icon.offsetWidth; // restart the animation
        icon.classList.add('rbt-bump');
      }).observe(icon, { subtree: true, childList: true, characterData: true });
    });
  };
  if (!reduced) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCartIcons);
    else watchCartIcons();
  }

  if (
    window.Shopify?.designMode ||
    !('IntersectionObserver' in window) ||
    reduced
  ) {
    return;
  }

  const SELECTOR = [
    '.rbt-formules__heading',
    '.rbt-compare',
    '.rbt-formules__card',
    '.rbt-avis__score-panel',
    '.rbt-avis__featured',
    '.rbt-avis__card',
    '.rbt-videos__head',
    '.rbt-videos__item',
    '.rbt-guide__text',
    '.rbt-guide__help',
    '.rbt-faq__compact',
    '.rbt-cintro__chips',
    '.resource-list__item',
  ].join(',');

  const pending = new Set();
  const show = (el) => {
    // Once faded in, hand the element back to its own styles (hover lifts, etc.).
    el.addEventListener('transitionend', () => el.classList.remove('rbt-reveal', 'is-inview'), { once: true });
    el.classList.add('is-inview');
    pending.delete(el);
    observer.unobserve(el);
  };

  const observer = new IntersectionObserver(
    (entries) => entries.forEach((entry) => entry.isIntersecting && show(entry.target)),
    { rootMargin: '0px 0px -8% 0px' }
  );

  // A fast fling can jump over an element between two frames, so the observer never sees it
  // intersect: anything already above the bottom of the screen is shown on the next scroll.
  let ticking = false;
  const sweep = () => {
    ticking = false;
    const fold = window.innerHeight;
    pending.forEach((el) => {
      if (el.getBoundingClientRect().top < fold) show(el);
    });
    if (!pending.size) window.removeEventListener('scroll', onScroll);
  };
  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(sweep);
    }
  };

  const init = () => {
    const fold = window.innerHeight;
    document.querySelectorAll(SELECTOR).forEach((el) => {
      // Skip what is already on screen, and cards parked sideways in a scroll track
      // (they would only fade in mid-swipe).
      const rect = el.getBoundingClientRect();
      if (rect.top < fold || rect.left >= window.innerWidth) return;
      // Siblings appear one after the other, 70 ms apart, at most 4 steps.
      const index = [...el.parentElement.children].indexOf(el);
      el.style.setProperty('--rbt-reveal-delay', `${Math.min(index, 3) * 70}ms`);
      el.classList.add('rbt-reveal');
      pending.add(el);
      observer.observe(el);
    });
    if (pending.size) window.addEventListener('scroll', onScroll, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
